import { UserRole, type NavItem } from '../types';
import {
    LayoutDashboard,
    Search,
    Wallet,
    Receipt,
    Settings,
    HelpCircle,
    Users,
    CreditCard,
    Trophy,
    BadgeDollarSign,
    GraduationCap,
    Inbox,
    Headset,
    Stethoscope,
    Tag,
    FlaskConical,
    LayoutList,
    TicketPercent,
    ReceiptText,
    FileSignature,
    Newspaper,
    ShieldCheck,
    ShieldAlert,
    PhoneCall,
} from 'lucide-react';

// Define navigation items with role-based access
export const navigationConfig: NavItem[] = [
    {
        id: 'dashboard',
        label: 'Dashboard',
        path: '/dashboard',
        icon: LayoutDashboard,
        roles: [UserRole.EMPLOYER, UserRole.AGENT, UserRole.SUPER_ADMIN],
    },
    {
        id: 'hr-dashboard',
        label: 'Dashboard',
        path: '/hr/dashboard',
        icon: LayoutDashboard,
        roles: [UserRole.HR],
    },
    {
        id: 'cs-dashboard',
        label: 'Dashboard',
        path: '/customer-success',
        icon: LayoutDashboard,
        roles: [UserRole.CUSTOMER_SUCCESS],
    },
    {
        id: 'cs-investigations',
        label: 'Investigations',
        path: '/customer-success/investigations',
        icon: Headset,
        roles: [UserRole.CUSTOMER_SUCCESS],
    },
    {
        id: 'medical-dashboard',
        label: 'Dashboard',
        path: '/medical',
        icon: LayoutDashboard,
        roles: [UserRole.MEDICAL],
    },
    {
        id: 'medical-reports',
        label: 'Medical Reports',
        path: '/medical/investigations',
        icon: Stethoscope,
        roles: [UserRole.MEDICAL],
    },
    {
        id: 'notary-assignments',
        label: 'Notary',
        path: '/notary/investigations',
        icon: FileSignature,
        roles: [UserRole.NOTARY],
    },
    {
        id: 'police-assignments',
        label: 'Police',
        path: '/police/investigations',
        icon: ShieldCheck,
        roles: [UserRole.POLICE],
    },
    {
        id: 'investigations',
        label: 'Investigations',
        path: '/investigations',
        icon: Search,
        roles: [UserRole.EMPLOYER, UserRole.AGENT, UserRole.SUPER_ADMIN],
    },
    {
        id: 'hr-earnings',
        label: 'Earnings',
        path: '/hr/earnings',
        icon: BadgeDollarSign,
        roles: [UserRole.HR],
    },
    {
        id: 'hr-leaderboard',
        label: 'Leaderboard',
        path: '/hr/leaderboard',
        icon: Trophy,
        roles: [UserRole.HR],
    },
    {
        id: 'transactions',
        label: 'Transactions',
        path: '/transactions',
        icon: Receipt,
        roles: [UserRole.EMPLOYER],
    },
    {
        id: 'employee-reports',
        label: 'Report an employee',
        path: '/employee-reports',
        icon: ShieldAlert,
        roles: [UserRole.EMPLOYER],
    },
    {
        id: 'admin-employee-reports',
        label: 'Employee reports',
        path: '/admin/employee-reports',
        icon: ShieldAlert,
        roles: [UserRole.SUPER_ADMIN, UserRole.CUSTOMER_SUCCESS],
    },
    {
        id: 'earnings',
        label: 'Earnings',
        path: '/earnings',
        icon: Wallet,
        roles: [UserRole.AGENT, UserRole.CUSTOMER_SUCCESS, UserRole.MEDICAL],
    },
    {
        id: 'withdrawals',
        label: 'Withdrawals',
        path: '/withdrawals',
        icon: CreditCard,
        roles: [UserRole.AGENT, UserRole.HR, UserRole.SUPER_ADMIN, UserRole.CUSTOMER_SUCCESS, UserRole.MEDICAL],
    },
    {
        id: 'users',
        label: 'Users',
        path: '/users',
        icon: Users,
        roles: [UserRole.SUPER_ADMIN],
    },
    {
        id: 'pricing',
        label: 'Pricing',
        path: '/pricing',
        icon: Tag,
        roles: [UserRole.SUPER_ADMIN],
    },
    {
        id: 'plans',
        label: 'Plans',
        path: '/plans',
        icon: LayoutList,
        roles: [UserRole.SUPER_ADMIN],
    },
    {
        id: 'medical-labs',
        label: 'Medical Labs',
        path: '/medical-labs',
        icon: FlaskConical,
        roles: [UserRole.SUPER_ADMIN],
    },
    {
        id: 'promo-codes',
        label: 'Promo Codes',
        path: '/promo-codes',
        icon: TicketPercent,
        roles: [UserRole.SUPER_ADMIN],
    },
    {
        id: 'admin-transactions',
        label: 'Transactions',
        path: '/admin/transactions',
        icon: ReceiptText,
        roles: [UserRole.SUPER_ADMIN],
    },
    {
        id: 'callbacks',
        label: 'Callbacks',
        path: '/callbacks',
        icon: PhoneCall,
        roles: [UserRole.SUPER_ADMIN, UserRole.CUSTOMER_SUCCESS],
    },
    {
        id: 'blog-admin',
        label: 'Blog',
        path: '/admin/blog',
        icon: Newspaper,
        roles: [UserRole.SUPER_ADMIN],
    },
    {
        id: 'training',
        label: 'Training',
        path: '/training',
        icon: GraduationCap,
        roles: [UserRole.AGENT],
    },
    {
        id: 'training-admin',
        label: 'Training',
        path: '/training/admin',
        icon: GraduationCap,
        roles: [UserRole.SUPER_ADMIN],
    },
    {
        id: 'messages',
        label: 'Messages',
        path: '/admin/messages',
        icon: Inbox,
        roles: [UserRole.SUPER_ADMIN],
    },
    {
        id: 'settings',
        label: 'Settings',
        path: '/settings',
        icon: Settings,
        roles: [
            UserRole.EMPLOYER,
            UserRole.AGENT,
            UserRole.HR,
            UserRole.SUPER_ADMIN,
            UserRole.CUSTOMER_SUCCESS,
            UserRole.MEDICAL,
        ],
    },
    {
        id: 'support',
        label: 'Support',
        path: '/support',
        icon: HelpCircle,
        roles: [
            UserRole.EMPLOYER,
            UserRole.AGENT,
            UserRole.HR,
            UserRole.SUPER_ADMIN,
            UserRole.CUSTOMER_SUCCESS,
            UserRole.MEDICAL,
        ],
    },
];

// Helper function to filter navigation based on user role
export const getNavigationForRole = (role: UserRole | undefined): NavItem[] => {
    if (!role) {
        return [];
    }
    return navigationConfig.filter((item) => item.roles?.includes(role));
};

// Helper function to check if user can request investigation or submit report
export const canRequestInvestigation = (role: UserRole | undefined): boolean => {
    return role === UserRole.EMPLOYER || role === UserRole.AGENT;
};
